import { upsertNotification } from './merge-notification'

/**
 * The provider's state as one reducer (board card 7u11): every transition is
 * a function of the previous state, so a stream event that lands in the
 * same tick as a `loadMore` page can never clobber it — React applies both
 * in order — and the badge bump is decided against the same `prev` the
 * upsert saw, never a stale snapshot. Pure: safe under StrictMode's double
 * invocation.
 */
export interface NotificationStateItem {
  id: string
  category: 'notification' | 'activity'
  isRead: boolean
  notifiedAt: string
}

export interface NotificationBadge {
  unreadCount: number
  hasUnseenActivity: boolean
}

export interface NotificationState<T extends NotificationStateItem> {
  items: T[]
  badge: NotificationBadge
  cursor: string | null
  hasMore: boolean
}

export type NotificationAction<T extends NotificationStateItem> =
  /** The first page from notifications.list. */
  | { type: 'loaded'; items: T[]; nextCursor: string | null }
  /** A further page appended by loadMore. */
  | { type: 'append'; items: T[]; nextCursor: string | null }
  /** One row from the SSE stream (event `notification` or `activity`). */
  | { type: 'stream'; item: T }
  /** The server's badge (initial fetch, stream `badge` event, after mark-read). */
  | { type: 'badge'; badge: NotificationBadge }
  | { type: 'markRead'; id: string }
  | { type: 'markAllRead'; category?: 'notification' | 'activity' }

export const initialNotificationState: NotificationState<never> = {
  items: [],
  badge: { unreadCount: 0, hasUnseenActivity: false },
  cursor: null,
  hasMore: true,
}

export function notificationReducer<T extends NotificationStateItem>(
  state: NotificationState<T>,
  action: NotificationAction<T>
): NotificationState<T> {
  switch (action.type) {
    case 'loaded':
      return {
        ...state,
        items: action.items,
        cursor: action.nextCursor,
        hasMore: action.nextCursor !== null,
      }
    case 'append':
      return {
        ...state,
        items: [...state.items, ...action.items],
        cursor: action.nextCursor,
        hasMore: action.nextCursor !== null,
      }
    case 'stream': {
      // A coalesced row (re-mention, new reply) replaces its stale item and
      // moves to the top; the badge is bumped optimistically from the same
      // `prev` — the server's badge event follows and wins.
      const { items, newlyUnread } = upsertNotification(state.items, action.item)
      let badge = state.badge
      if (action.item.category === 'activity') {
        if (!action.item.isRead && !badge.hasUnseenActivity)
          badge = { ...badge, hasUnseenActivity: true }
      } else if (newlyUnread) {
        badge = { ...badge, unreadCount: badge.unreadCount + 1 }
      }
      return { ...state, items, badge }
    }
    case 'badge':
      return { ...state, badge: action.badge }
    case 'markRead':
      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.id ? { ...item, isRead: true } : item
        ),
      }
    case 'markAllRead':
      return {
        ...state,
        items: state.items.map((item) =>
          !action.category || item.category === action.category ? { ...item, isRead: true } : item
        ),
      }
  }
}
